import React from 'react';
import { isFlagOn, isInPricingPlan } from '../../../app/utils/flags';
import { Segment, Tour } from 'src/types';
import { getGlobalProject } from 'src/utils/globals';
import { EventRefine, EventRow } from './EventRow';
import UserInteractivitySettingsRow from './UserInteractivitySettingsRow';
import { UpgradeTierButton, UpgradeTierInfoSmall } from 'src/components/UpgradeTier';
import { Checklist } from 'src/data/types/base';
import { SurveysLoader } from './DynamicRows';
import { RefinedRule, RuleDescriptor, RuleExpression } from 'src/data/types/descriptor';

export type OperatorType = {
    value: string;
    text: string;
    inputPostfix?: string;
    displayText?: string;
    hasValue?: boolean;
}

export const generalOperators = [
    { value: '=', text: 'equals' },
    { value: '!=', text: 'doesn\'t equal' },
    { value: '>', text: 'is larger than' },
    { value: '<', text: 'is less than' },
    { value: '>=', text: 'is larger or equals' },
    { value: '<=', text: 'is less or equals' },
    { value: 'contains', text: 'contains' },
    { value: 'contains_not', text: 'doesn\'t contain' },
    { value: 'is_empty', text: 'is empty', hasValue: false },
    { value: 'is_empty_not', text: 'is not empty', hasValue: false },
    { value: 'rgx', text: 'matches regex' },
    { value: 'rgx_not', text: 'doesn\'t match regex' }
] as Array<OperatorType>;

export const generalWideOperators = [
    { value: '=', text: 'equals' },
    { value: '!=', text: 'doesn\'t equal' },
    { value: '>', text: 'is larger than' },
    { value: '<', text: 'is less than' },
    { value: '>=', text: 'is larger or equals' },
    { value: '<=', text: 'is less or equals' },
    { value: 'contains', text: 'contains' },
    { value: 'contains_not', text: 'doesn\'t contain' },
    { value: 'is_empty', text: 'is empty', hasValue: false },
    { value: 'is_empty_not', text: 'is not empty', hasValue: false },
    { value: 'd_ago', text: 'is at least', inputPostfix: 'days ago' },
    { value: 'd_ago_max', text: 'was no more than', inputPostfix: 'days ago' },
    { value: 'rgx', text: 'matches regex' },
    { value: 'rgx_not', text: 'doesn\'t match regex' }
] as Array<OperatorType>;

export type PredefinedPropType = {
    value: string;
    text: string;
    displayText?: string;
    group?: string;
    className?: string;
    operators?: Array<OperatorType>;
    opts?: Array<{ value: string, text: string }>;
    customAttrComponent?: Function;
    customRefineComponent?: Function;
    customSettingsComponent?: Function;
    hasValue?: boolean;
}

type GroupsSettings = {
    [index: string]: {
        allowCreating?: boolean
    }
}

export const allGroupsSettings: GroupsSettings = {
    'user': {
        allowCreating: true
    }
}

export type RulesPropsOptions = {
    supportsEvents?: boolean;
    supportsUserInteractivity?: boolean;
    supportsChecklistStates?: boolean;
    supportsSurveys?: boolean;
    supportsAnnoucements?: boolean;
}

export const getPredefinedProps = (allTours?: Array<any>, allSegments?: Array<any>, allChecklists?: Array<any>, opts?: RulesPropsOptions): PredefinedPropType[] => {
    const project = getGlobalProject();

    const predefinedProps = [
        { value: 'user.role', text: 'Role', group: 'User property', className: '' },
        { value: 'user.username', text: 'Username', group: 'User property', className: '' },
        { value: 'user.email', text: 'E-mail', group: 'User property', className: '' },
        {
            value: 'user.signUpAt',
            text: 'Sign up date',
            group: 'User property',
            className: '',
            operators: [
                { value: '=', text: 'equals' },
                { value: '!=', text: 'doesn\'t equal' },
                { value: '>', text: 'is larger than' },
                { value: '<', text: 'is less than' },
                { value: '>=', text: 'is larger or equals' },
                { value: '<=', text: 'is less or equals' },
                { value: 'contains', text: 'contains' },
                { value: 'contains_not', text: 'doesn\'t contain' },
                { value: 'is_empty', text: 'is empty', hasValue: false },
                { value: 'is_empty_not', text: 'is not empty', hasValue: false },
                { value: 'd_ago', text: 'is at least', inputPostfix: 'days ago' },
                { value: 'd_ago_max', text: 'was no more than', inputPostfix: 'days ago' },
            ] as Array<OperatorType>
        },
        { value: 'user.firstname', text: 'First name', group: 'User property', className: '' },
        { value: 'user.lastname', text: 'Last name', group: 'User property', className: '' },
        {
            value: 'env.language',
            text: 'Language',
            opts: project.SupportedLanguages.map(i => {
                return {
                    value: i,
                    text: i
                };
            }),
            operators: [
                { value: '=', text: 'is' },
                { value: '!=', text: 'is not' }
            ] as Array<OperatorType>,
            group: 'Language',
            className: ''
        },
        {
            value: 'device.type',
            text: 'Device type',
            opts: [
                { value: 'mobile', text: 'Mobile' },
                { value: 'desktop', text: 'Desktop' },
            ],
            operators: [
                { value: '=', text: 'is' },
                { value: '!=', text: 'is not' }
            ] as Array<OperatorType>,
            group: 'Device',
            className: ''
        }
    ];

    if (allTours && allTours.length > 0) {
        const tourOperators = [
            { value: 'tour_state_valid', text: 'applies for', displayText: 'is' },
            { value: 'tour_state_invalid', text: 'doesn\'t apply for', displayText: 'is not' }
        ] as Array<OperatorType>;
        const tours = allTours.map(tour => ({ text: tour.name, value: tour.id }));

        Array.prototype.push.apply(predefinedProps, [
            { value: 'tour.fin', text: 'Finished', group: 'Tour state', className: '', opts: tours, operators: tourOperators, displayText: 'finished' },
            { value: 'tour.skip', text: 'Skipped', group: 'Tour state', className: '', opts: tours, operators: tourOperators, displayText: 'skipped' },
            { value: 'tour.fin_skip', text: 'Finished or skipped', group: 'Tour state', className: '', opts: tours, operators: tourOperators, displayText: 'finished or skipped' },
        ]);
    }

    if (allSegments && allSegments.length > 0) {
        const segmentOperators = [
            { value: 'segment_match', text: 'Is member of', displayText: 'matches' },
            { value: 'segment_match_not', text: 'Is not member of', displayText: 'doesn\'t match' }
        ] as Array<OperatorType>;

        const segments = allSegments.map(segment => ({ text: segment.name, value: segment.id }));

        Array.prototype.push.apply(predefinedProps, [
            { value: 'segment.match', text: 'Segment', group: 'Segments', className: '', opts: segments, operators: segmentOperators, displayText: ' ' },
        ]);
    }

    if (opts && opts.supportsEvents === true) {
        Array.prototype.push.apply(predefinedProps, [
            {
                value: 'event',
                text: 'Event',
                group: 'Event',
                customAttrComponent: !isInPricingPlan('be_pkg') ? UpgradeInfo : EventRow,
                operators: [
                    { value: 'exists', text: 'has happened' },
                    { value: 'not_exists', text: 'has not happened' },
                ] as Array<OperatorType>,
                hasValue: false,
                customRefineComponent: EventRefine
            },
        ]);
    }

    if (opts && opts.supportsUserInteractivity) { // hide it temporarily
        Array.prototype.push.apply(predefinedProps, [
            {
                value: 'uiact.inputVal',
                text: 'Input has a value',
                group: 'UI state',
                customSettingsComponent: UserInteractivitySettingsRow,
                operators: [
                    { value: '=', text: 'equals' },
                    { value: '!=', text: 'doesn\'t equal' },
                    { value: 'contains', text: 'contains' },
                    { value: 'contains_not', text: 'doesn\'t contain' },
                    { value: 'empty', text: 'is empty' },
                    { value: 'empty_not', text: 'is not empty' },
                ]
            },
            // {
            //     value: 'uiact.inputChange',
            //     text: 'User fills an input',
            //     group: 'UI state',
            //     customSettingsComponent: UserInteractivitySettingsRow
            // },
            {
                value: 'uiact.elState',
                text: 'UI element',
                group: 'UI state',
                operators: [
                    { value: 'exists', text: 'exists on the page' },
                    { value: 'not_exists', text: 'does not exist on the page' },
                ],
                customSettingsComponent: UserInteractivitySettingsRow
            },
        ]);
    }

    if (allChecklists && opts && opts.supportsChecklistStates) {
        const checklistsOperators = [
            { value: 'chklst_state_valid', text: 'applies for', displayText: 'is' },
            // { value: 'chklst_state_invalid', text: 'doesn\'t apply for', displayText: 'is not' }
        ] as Array<OperatorType>;
        const checklists = allChecklists.map(tour => ({ text: tour.name, value: tour.id }));

        Array.prototype.push.apply(predefinedProps, [
            { value: 'chklst.compl', text: 'Completed', group: 'Checklist state', className: '', opts: checklists, operators: checklistsOperators, displayText: 'completed' },
            { value: 'chklst.dismis', text: 'Dismissed', group: 'Checklist state', className: '', opts: checklists, operators: checklistsOperators, displayText: 'dismissed' },
            { value: 'chklst.compl_dismis', text: 'Completed or dismissed', group: 'Checklist state', className: '', opts: checklists, operators: checklistsOperators, displayText: 'completed or dismissed' },
            // { value: 'chklst.in_prgrs', text: 'In progress', group: 'Checklist state', className: '', opts: checklists, operators: checklistsOperators, displayText: 'in progress' },
        ]);
    }

    // if (opts?.supportsSurveys && isFlagOn('sgmext')) {
        const surveyOperators = [
            { value: 'surv_is', text: 'applies for', displayText: 'is' },
            { value: 'surv_not', text: 'doesn\'t apply for', displayText: 'is not' }
        ] as Array<OperatorType>;

        Array.prototype.push.apply(predefinedProps, [
            { value: 'surv.fin', text: 'Fully answered', group: 'Survey state', className: '', operators: surveyOperators, displayText: 'fully answered', hasValue: false, customRefineComponent: SurveysLoader },
            { value: 'surv.skip', text: 'Closed, partially or not answered', group: 'Survey state', className: '', operators: surveyOperators, displayText: 'closed, partially or not answered', hasValue: false, customRefineComponent: SurveysLoader },
            // { value: 'surv.fin_skip', text: 'Answered or skipped', group: 'Survey state', className: '', operators: surveyOperators, displayText: 'anytime closed or fully answered', hasValue: false, customRefineComponent: SurveysLoader },
        ]);
    // }

    // Array.prototype.push.apply(predefinedProps, [
    //     { value: '', text: 'Custom property', className: 'rules-custom' },
    // ]);

    return predefinedProps;
}

export type GroupedPredefinedProps = {
    label: string;
    groupId: string;
    attributes: PredefinedProp[];
}

export type PredefinedProp = PredefinedPropType & { attrId: string };
export type GroupedPropsResult = GroupedPredefinedProps;

export function getGroupedProps(predefinedProps: Array<PredefinedPropType>) {
    let res: Array<GroupedPropsResult> = [];

    for (const prop of predefinedProps) {
        if (prop.group) {
            const [groupId, propId] = getPropIds(prop.value);

            let existingGroup = res.filter((g: GroupedPredefinedProps) => g.label == prop.group)[0];
            if (!existingGroup) {
                existingGroup = { groupId, label: prop.group, attributes: [] };
                res.push(existingGroup);
            }
            existingGroup.attributes.push({ ...prop, attrId: propId });
        } else {
            console.error(prop);
            throw new Error('props without groups not supported in the new rules editor');
        }
    }

    return res;
}

export const getPropIds = (prop: string): [string, string] => {
    const [groupId, ...rest] = prop.split('.');
    const propId = rest.join('.');

    return [groupId, propId];
}

export function useBasicDefinitionForProp(
    prop: string,
    allTours: Tour[],
    allSegments: Segment[],
    allChecklists: Checklist[],
    readMode: boolean,
    opts?: RulesPropsOptions): [GroupedPredefinedProps[], PredefinedProp[], PredefinedProp] {
    const predefinedProps = getPredefinedProps(allTours, allSegments, allChecklists, opts);
    const groupedPredefinedProps = getGroupedProps(predefinedProps);
    const [groupId, _] = getPropIds(prop);

    const attrsOfGroup = groupedPredefinedProps.filter(i => i.groupId == groupId)[0]?.attributes || [];
    let attrConfig: PredefinedProp = attrsOfGroup.filter(attr => {
        const isGroupOnly = attr.value.indexOf('.') === -1;
        if (isGroupOnly && attr.value == groupId) {
            return true;
        } /*else if (!isGroupOnly && prop.startsWith('user.props.') && attr.value.startsWith('user.')) {
            return true;
        }*/ else if (!isGroupOnly && attr.value == prop) {
            return true;
        }

        return false;
    })[0] || {} as PredefinedProp;

    if (readMode) {
        if (prop.startsWith('user.props.')) {
            const cleanProp = prop.replace('user.props.', '');
            attrConfig = {
                value: prop,
                text: cleanProp,
                attrId: ''
            }
        }
    }

    return [groupedPredefinedProps, attrsOfGroup, attrConfig];
}

function UpgradeInfo() {
    return <div className='d-flex flex-column align-items-center'>
        <UpgradeTierButton size='sm' hintColumn={true} showHint={true} />
    </div>
}

export type CustomRefineComponentProps = {
    rule: RuleDescriptor;
    onRefinedRuleChanged: (r: RefinedRule) => void;
    onAddRefine: () => void;
    onRemoveRefine: () => void;
    onRuleExpressionChanged: (r: RuleExpression) => void;
}